import { apiBase } from '@/api/http';

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'];
const ADMIN_API_PREFIX = /^\/(?:dev-api|prod-api|stage-api)(?=\/)/i;

export type ProductFileItem = {
  raw: string;
  url: string;
  name: string;
  ext: string;
  isImage: boolean;
  isPdf: boolean;
};

/** 上传文件由后端挂在 /profile，不走 /api */
function assetOrigin(): string {
  const assetBase = import.meta.env.VITE_ASSET_BASE;
  if (typeof assetBase === 'string' && assetBase.trim()) {
    return assetBase.trim().replace(/\/$/, '');
  }

  const api = apiBase().trim();
  if (/^https?:\/\//i.test(api)) {
    try {
      return new URL(api).origin;
    } catch {
      return '';
    }
  }

  return '';
}

function normalizeStoredPath(path: string): string {
  let value = path.trim();
  if (!value) {
    return '';
  }

  value = value.replace(ADMIN_API_PREFIX, '');

  if (/^https?:\/\//i.test(value)) {
    try {
      const url = new URL(value);
      url.pathname = url.pathname.replace(ADMIN_API_PREFIX, '');
      return url.toString();
    } catch {
      return value;
    }
  }

  if (!value.startsWith('/')) {
    value = `/${value}`;
  }

  return value;
}

/** Rewrite known third-party CDN hosts to local public assets when possible. */
function rewriteExternalAssetHost(url: string): string {
  try {
    const parsed = new URL(url);
    if (parsed.hostname === 'app.wkyqinc.com') {
      return parsed.pathname || '/';
    }
  } catch {
    // ignore invalid URL
  }
  return url;
}

export function resolveAssetUrl(path?: string | null): string {
  const raw = String(path || '').trim();
  if (!raw) {
    return '';
  }

  if (/^(data|blob):/i.test(raw)) {
    return raw;
  }

  if (/^https?:\/\//i.test(raw)) {
    return rewriteExternalAssetHost(normalizeStoredPath(raw));
  }

  const normalized = normalizeStoredPath(raw);
  if (!normalized) {
    return '';
  }

  if (/^https?:\/\//i.test(normalized)) {
    return normalized;
  }

  const origin = assetOrigin();
  if (origin) {
    return `${origin}${normalized}`;
  }

  return normalized;
}

export function parseProductTags(tags?: string | null): string[] {
  return String(tags || '')
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
}

export function parseProductFiles(files?: string | null): ProductFileItem[] {
  return String(files || '')
    .split(',')
    .map((file) => file.trim())
    .filter(Boolean)
    .map((raw) => {
      const name = fileNameFromPath(raw);
      const ext = fileExtension(name);
      return {
        raw,
        url: resolveAssetUrl(raw),
        name,
        ext,
        isImage: IMAGE_EXTENSIONS.includes(ext),
        isPdf: ext === 'pdf',
      };
    });
}

export function isApiProduct(product?: { productType?: string | null } | null): boolean {
  return product?.productType === 'API';
}

/** 列表/详情大字号数字部分 */
export function formatProductPriceAmount(
  price: number | string,
  _product?: { productType?: string | null; pointsCost?: number | null },
): string {
  const n = Number(price);
  return Number.isFinite(n) ? String(n) : String(price);
}

export function formatProductPriceUnit(_product?: { productType?: string | null } | null): string {
  return 'U';
}

/** 兼容旧用法：数字 + U */
export function formatProductPrice(price: number | string): string {
  return `${formatProductPriceAmount(price)} U`;
}

function parseBalanceAmount(amount: number | string | undefined | null): number {
  if (amount == null || amount === '') return 0;
  const n = Number(amount);
  return Number.isFinite(n) ? n : 0;
}

/** 仅数字字符串（两位小数），用于大字号 + 旁侧 U */
export function formatBalanceAmount(amount: number | string | undefined | null): string {
  return parseBalanceAmount(amount).toFixed(2);
}

/** 余额/订单等：数字 + 后缀 U，如 "128.50 U" */
export function formatBalanceU(amount: number | string | undefined | null): string {
  return `${formatBalanceAmount(amount)} U`;
}

function looksLikeHtml(content: string): boolean {
  return /<\s*[a-z][\s\S]*>/i.test(content);
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function rewriteHtmlAssetUrls(html: string): string {
  if (!html) {
    return '';
  }

  return html.replace(/(src|href)\s*=\s*["']([^"']+)["']/gi, (_match, attr: string, url: string) => {
    const trimmed = url.trim();
    if (!trimmed || trimmed.startsWith('#') || /^mailto:/i.test(trimmed)) {
      return `${attr}="${trimmed}"`;
    }
    if (/^(data|blob):/i.test(trimmed)) {
      return `${attr}="${trimmed}"`;
    }
    return `${attr}="${resolveAssetUrl(trimmed)}"`;
  });
}

export function sanitizeHtml(html: string): string {
  if (!html) {
    return '';
  }
  if (typeof window === 'undefined') {
    return rewriteHtmlAssetUrls(html);
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('script, iframe, object, embed, link, meta, style').forEach((node) => node.remove());
  doc.body.querySelectorAll('*').forEach((element) => {
    Array.from(element.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = attr.value.trim().toLowerCase();
      if (name.startsWith('on') || value.startsWith('javascript:')) {
        element.removeAttribute(attr.name);
      }
    });
  });

  return rewriteHtmlAssetUrls(doc.body.innerHTML);
}

/** 纯文本保留换行；富文本保留 HTML 并修正图片等资源地址 */
export function formatDetailContent(content?: string | null): string {
  const raw = String(content || '').trim();
  if (!raw) {
    return '';
  }

  if (looksLikeHtml(raw)) {
    return sanitizeHtml(raw);
  }

  return sanitizeHtml(escapeHtml(raw).replace(/\r\n/g, '\n').replace(/\n/g, '<br />'));
}

function fileNameFromPath(path: string): string {
  const clean = path.split('?')[0] || '';
  const parts = clean.split('/');
  return decodeURIComponent(parts[parts.length - 1] || '文件');
}

function fileExtension(name: string): string {
  const index = name.lastIndexOf('.');
  return index >= 0 ? name.slice(index + 1).toLowerCase() : '';
}
